/**
 * Track B recommendation cards (INC-1007, UI half): one read-only card per (name, horizon).
 * Owning vertical: Track B. The view has no controls and issues no write: Track B never trades.
 * A score orders names and is not a probability; a probability appears only when the server
 * states one (calibrated), otherwise the card says "uncalibrated". Every number carries the
 * instant it was known (IST) and the source manifests (short hash) it came from; an absent
 * value reads "no value", never a blank that could be taken for zero.
 */
import { badge, definitions, instantCell, scrollRegion } from "../../app/components.ts";
import { h, type Child } from "../../app/dom.ts";
import { formatExchangeDate } from "../../app/format.ts";
import { stateOfEnvelope } from "../../app/state.ts";
import type { ViewContext, ViewHandle } from "../../app/view.ts";
import { mountOps, panel, shortHash, simpleTable } from "../ops-common.ts";
import {
  TRACKB_PROJECTION,
  decodeTrackbRow,
  type Beta,
  type Component,
  type Contribution,
  type Source,
  type TrackbRow,
} from "./contract.ts";
import { groupByHorizon, reasonText, sourcesText } from "./model.ts";
import "../../styles/trackb.css";

/** A value that may be absent: its text, or "no value" (never zero, never empty). */
export function value(text: string | number | null): HTMLElement {
  return text === null
    ? h("span", { class: "metric metric-missing" }, "no value")
    : h("span", { class: "metric" }, String(text));
}

const day = (text: string | null): HTMLElement =>
  value(text === null ? null : formatExchangeDate(text));

/** A section of a card: heading, its facts, then the as-of and sources every number carries. */
function part(
  id: string,
  title: string,
  asOf: string,
  sources: readonly Source[],
  ...content: Child[]
): HTMLElement {
  return h(
    "section",
    { class: "tb-part", attrs: { "aria-labelledby": id } },
    h("h4", { attrs: { id } }, title),
    ...content,
    h(
      "p",
      { class: "note tb-provenance" },
      "As of ",
      instantCell(asOf),
      `. Sources (manifest): ${sourcesText(sources)}.`,
    ),
  );
}

function factors(row: TrackbRow, id: string): HTMLElement {
  const f = row.factors;
  const line = (c: Contribution): Child[] => [
    c.feature,
    c.weight,
    value(c.raw),
    value(c.zscore),
    value(c.rank),
    value(c.contribution),
    instantCell(c.asOf),
    sourcesText(c.sources),
  ];
  return part(
    `${id}-factors`,
    "Factor contributions",
    f.asOf,
    f.sources,
    definitions([
      ["Score", value(f.score)],
      ["Rank", value(f.rank)],
      ["Factor version", f.version],
      ["Matrix", shortHash(f.matrixHash)],
      ["Weights", shortHash(f.weightsHash)],
    ]),
    scrollRegion(
      `${row.isin} ${row.horizon} factor contributions`,
      simpleTable(
        `${row.isin} ${row.horizon} factor contributions`,
        ["Feature", "Weight", "Raw", "Z-score", "Rank", "Contribution", "As of", "Source"],
        f.contributions.map(line),
      ),
    ),
  );
}

function sentiment(row: TrackbRow, id: string): HTMLElement {
  const s = row.sentiment;
  return part(
    `${id}-sentiment`,
    "Sentiment",
    s.asOf,
    s.sources,
    definitions([
      ["Value (-1 to 1)", value(s.value)],
      ["Weight", s.weight],
      ["Replies used", String(s.replies.length)],
      ["Replies available", s.availableAt === null ? value(null) : instantCell(s.availableAt)],
      ["Model", s.modelId],
      ["Prompt", shortHash(s.promptHash)],
      ["Decay (sessions)", String(s.decaySessions)],
      ["Formula", s.formula],
    ]),
  );
}

function betaCells(b: Beta): Child[] {
  return b.kind === "fit"
    ? [
        b.factor,
        b.beta,
        b.standardError,
        b.ridgeLambda,
        String(b.observations),
        `${formatExchangeDate(b.firstSession)} to ${formatExchangeDate(b.lastSession)}`,
        String(b.stage),
        b.unit,
      ]
    : [
        b.factor,
        `No beta: ${reasonText(b.noBeta)} (${b.noBeta})`,
        value(null),
        value(null),
        value(null),
        value(null),
        value(null),
        value(null),
      ];
}

function macro(row: TrackbRow, id: string): HTMLElement {
  const m = row.macro;
  return part(
    `${id}-macro`,
    "Macro exposure",
    m.asOf,
    m.sources,
    definitions([
      ["Sector index", value(m.sectorIndex)],
      ["Window (sessions)", String(m.windowSessions)],
      ["Observations", value(m.observations)],
      ["Exposure", m.exposureHash === null ? value(null) : shortHash(m.exposureHash)],
      ["Registration", shortHash(m.registrationHash)],
    ]),
    m.unavailable === null
      ? scrollRegion(
          `${row.isin} ${row.horizon} macro betas`,
          simpleTable(
            `${row.isin} ${row.horizon} macro betas`,
            [
              "Factor",
              "Beta",
              "Standard error",
              "Ridge lambda",
              "Observations",
              "Window",
              "Stage",
              "Unit",
            ],
            m.betas.map(betaCells),
          ),
        )
      : h(
          "p",
          { class: "note", attrs: { "data-unavailable": m.unavailable } },
          `Macro exposure unavailable: ${reasonText(m.unavailable)} (${m.unavailable}). No beta is shown.`,
        ),
  );
}

function regime(row: TrackbRow, id: string): HTMLElement {
  const r = row.regime;
  const line = (c: Component): Child[] => [c.component, value(c.raw), value(c.score), c.weight];
  return part(
    `${id}-regime`,
    "Market regime",
    r.asOf,
    r.sources,
    definitions([
      ["Session", formatExchangeDate(r.session)],
      ["Composite (0 to 100)", value(r.composite)],
      ["Label", value(r.label)],
      ["Session rule", r.sessionRule],
      ["Reading", shortHash(r.readingHash)],
      ["Recipe", shortHash(r.recipeHash)],
    ]),
    scrollRegion(
      `${row.isin} ${row.horizon} regime components`,
      simpleTable(
        `${row.isin} ${row.horizon} regime components`,
        ["Component", "Raw", "Score (0 to 100)", "Weight"],
        r.components.map(line),
      ),
    ),
  );
}

function upside(row: TrackbRow, id: string): HTMLElement {
  const u = row.upside;
  return part(
    `${id}-upside`,
    "Upside inputs",
    u.asOf,
    u.sources,
    definitions([
      ["Period", u.period],
      ["Estimate dispersion", value(u.dispersion)],
      ["Estimates", String(u.estimates)],
      ["Window (sessions)", String(u.windowSessions)],
      ["Revisions up", value(u.revisionsUp)],
      ["Revisions down", value(u.revisionsDown)],
      ["Revisions net", value(u.revisionsNet)],
      ["Results in (days)", value(u.resultsDays)],
      ["Results date", day(u.resultsDate)],
      ["Corporate action in (days)", value(u.corporateDays)],
      ["Corporate action date", day(u.corporateDate)],
      ["Inputs available", u.availableAt === null ? value(null) : instantCell(u.availableAt)],
    ]),
  );
}

function universe(row: TrackbRow, id: string): HTMLElement {
  const u = row.universe;
  return part(
    `${id}-universe`,
    "Universe",
    u.asOf,
    u.sources,
    definitions([
      ["Names in the universe", String(u.size)],
      ["Listed from", formatExchangeDate(u.listedFrom)],
      ["Listing known", instantCell(u.listingAvailableAt)],
      ["Universe", shortHash(u.universeHash)],
    ]),
  );
}

function calibration(row: TrackbRow): HTMLElement {
  return row.probability === null
    ? h(
        "p",
        { class: "tb-calibration", attrs: { "data-calibration": row.calibration } },
        badge("Uncalibrated", "warn"),
        " No probability is stated: the rank orders names and is not a probability.",
      )
    : h(
        "p",
        { class: "tb-calibration", attrs: { "data-calibration": row.calibration } },
        badge("Calibrated", "ok"),
        ` Probability ${row.probability} (calibrated; card ${row.version}, manifest ${shortHash(row.manifestHash)}), as of `,
        instantCell(row.asOf),
        ".",
      );
}

function card(row: TrackbRow, index: number): HTMLElement {
  const id = `tb-${String(index)}`;
  return h(
    "article",
    {
      class: "tb-card",
      attrs: { "aria-labelledby": `${id}-title`, "data-entity": row.entityId },
    },
    h("h3", { attrs: { id: `${id}-title` } }, `${row.isin}, ${reasonText(row.horizon)} horizon`),
    calibration(row),
    definitions([
      ["Score", value(row.factors.score)],
      ["Rank", value(row.factors.rank)],
      ["As of", instantCell(row.asOf)],
      ["Card", `${row.version}, manifest ${shortHash(row.manifestHash)}`],
    ]),
    factors(row, id),
    sentiment(row, id),
    macro(row, id),
    regime(row, id),
    upside(row, id),
    universe(row, id),
  );
}

/**
 * Draw the cards read so far, grouped by horizon. When the envelope is stale, superseded or
 * cannot state its lag (`stale`), the cards are greyed and a sentence beside them says so; the
 * state banner above says why (review F5).
 */
export function drawTrackb(rows: readonly TrackbRow[], stale = false): readonly Child[] {
  let index = 0;
  return [
    h(
      "p",
      { class: "note" },
      "Track B recommends; it never trades, and this page has no controls. A score orders names and is not a probability. Every number shows when it was known (IST) and the source manifests it came from.",
    ),
    rows.length === 0 ? h("p", { class: "note" }, "No Track B cards are published yet.") : null,
    stale && rows.length > 0
      ? h(
          "p",
          { class: "note tb-stale-note" },
          "These cards are stale: they may no longer be current. Read each number at its stated time.",
        )
      : null,
    h(
      "div",
      { class: stale ? "tb-cards tb-stale" : "tb-cards", attrs: { "data-stale": String(stale) } },
      ...groupByHorizon(rows).map((group) =>
        panel(
          group.label,
          `tb-horizon-${group.horizon}`,
          ...group.rows.map((row) => card(row, (index += 1))),
        ),
      ),
    ),
  ];
}

/** Mount the Track B recommendation cards. */
export function mountTrackb(ctx: ViewContext): ViewHandle {
  return mountOps(ctx, {
    id: "trackb",
    title: "Track B recommendations",
    projection: TRACKB_PROJECTION,
    row: decodeTrackbRow,
    draw: (rows, _ctx, latest) =>
      drawTrackb(rows, ["stale", "unavailable"].includes(stateOfEnvelope(latest).kind)),
  });
}
